import { useState, useCallback, useRef } from 'react';
import { generateHotelAffiliateLink, generateFlightAffiliateLink, AFFILIATE_MARKER } from '../../utils/affiliate';
import { trackBookingClick, trackEvent as trackTPEvent } from '../../utils/tracking';
import { trackAffiliateRedirect } from '../../utils/analytics';
import { getBookingLangVariant } from '../../utils/abTest';
import { safePartnerUrlForType, partnerNameFromUrl } from '../../utils/partnerUrl';
import { useToastContext } from '../../contexts/ToastContext';

/**
 * Shared booking flow hook.
 *
 * Flow: showCheckoutPreview (modal) -> confirmBooking (overlay -> open affiliate)
 *
 * Use for HOTELS: showCheckoutPreview(hotel, params) where params has
 *   { city, checkin, checkout, guests, rooms }
 *
 * Use for FLIGHTS: bookFlight(flight) where flight has
 *   { departure, arrival, departure_at, return_at, deepLink, ... }
 *
 * - Partner referrals work for guests, without collecting a phone number.
 * - For flights, the "modal preview" step is skipped (just overlay -> redirect)
 *   because flights are time-sensitive and Aviasales already shows full detail.
 * - For hotels, the modal is shown for trust + transparency.
 *
 * MOBILE NOTE: We redirect in the SAME TAB (window.location.href) because
 * mobile browsers silently block `window.open('_blank')` calls wrapped in a
 * setTimeout (user-gesture context is lost). Same-tab is reliable everywhere.
 */
export default function useBookingFlow() {
  const [modalOpen, setModalOpen] = useState(false);
  const [overlayVisible, setOverlayVisible] = useState(false);
  const [pendingUrl, setPendingUrl] = useState(null);
  // iter80 — richer redirect summary context so the premium RedirectOverlay
  // can reassure the user with what they're actually booking (destination,
  // route, item, price, partner) instead of a generic spinner.
  const [redirectContext, setRedirectContext] = useState(null);
  const [activeHotel, setActiveHotel] = useState(null);
  const [activeParams, setActiveParams] = useState(null);
  // Auth gate is used only for protected account functions, not referrals.
  const [authGateOpen, setAuthGateOpen] = useState(false);
  // Keep the profile completion dialog API for account-only screens.
  const [profileGateOpen, setProfileGateOpen] = useState(false);
  const redirectTimerRef = useRef(null);
  const toast = useToastContext();

  const closeAuthGate = useCallback(() => {
    setAuthGateOpen(false);
  }, []);

  const closeProfileGate = useCallback(() => {
    setProfileGateOpen(false);
  }, []);

  /** Profile-gate resume: when the user saves their profile, replay the
   * deferred booking action with the same exact arguments. */
  // Only account-protected actions require a login; external search is open to everyone.
  const onProfileComplete = useCallback(() => setProfileGateOpen(false), []);

  /** Open the auth gate for a non-booking action (e.g. AI chat send). */
  const openAuthGate = useCallback((trigger = 'generic') => {
    trackTPEvent('auth_gate_shown', { trigger });
    setAuthGateOpen(true);
  }, []);

  const openCheckoutPreview = useCallback((hotel, params) => {
    trackBookingClick({
      type: 'hotel',
      hotel_name: hotel?.name,
      city: params?.city,
      price: hotel?.priceNumeric ?? hotel?.price,
    });
    setActiveHotel(hotel);
    setActiveParams(params);
    setModalOpen(true);
  }, []);

  const closeCheckoutPreview = useCallback(() => {
    setModalOpen(false);
  }, []);

  const dismissOverlay = useCallback(() => {
    if (redirectTimerRef.current) {
      clearTimeout(redirectTimerRef.current);
      redirectTimerRef.current = null;
    }
    setOverlayVisible(false);
    setPendingUrl(null);
    setRedirectContext(null);
  }, []);

  const performRedirect = useCallback((url, meta = {}) => {
    const destination = safePartnerUrlForType(url, meta.type);
    if (!destination) {
      toast.error('Линкът за резервация при партньора не е наличен. Моля, опитайте друга оферта.');
      return false;
    }
    // The overlay must describe the actual destination site even when a
    // supplier card carries stale, mismatched partner metadata.
    const actualHost = partnerNameFromUrl(destination);
    const actualPartner = actualHost ? actualHost.endsWith('.com') ? actualHost.slice(0, -4) : actualHost : 'unknown';
    // An affiliate redirect is a handoff, never a confirmed trip completion.
    // Phase-2 mandatory analytics — fired BEFORE we navigate. Payload keys
    // are stable per the Conversion & Trust Engine spec so GA4 dashboards
    // and internal funnels can slice by partner / type / surface / price.
    try {
      trackAffiliateRedirect({
        partner: partnerNameFromUrl(destination) || 'unknown',
        type: meta.type || 'unknown',
        origin: meta.origin || null,
        destination: meta.destination || null,
        price: meta.price ?? null,
        marker: AFFILIATE_MARKER || null,
        surface: meta.surface || null,
        reason_chips: meta.reason_chips || null,
        item_id: meta.item_id || null,
      });
    } catch { /* analytics must never break navigation */ }

    setPendingUrl(destination);
    setRedirectContext({ ...(meta || {}), partner: actualPartner });
    setOverlayVisible(true);
    // Short delay so the user sees the "securing your deal" animation,
    // then SAME-TAB navigation (reliable on mobile). Timer is stored in a
    // ref so dismissOverlay() can cancel it if the user taps the X first.
    if (redirectTimerRef.current) clearTimeout(redirectTimerRef.current);
    redirectTimerRef.current = setTimeout(() => {
      redirectTimerRef.current = null;
      try {
        window.location.assign(destination);
      } catch (err) {
        // If even that fails (rare), leave the overlay up so the user can tap
        // the manual fallback "Tap to continue" link inside RedirectOverlay.
        if (process.env.NODE_ENV === 'development') {
          console.warn('[useBookingFlow] window.location.href failed:', err);
        }
      }
    }, 900);
    return true;
  }, [toast]);

  const confirmBooking = useCallback(() => {
    if (!activeHotel || !activeParams) return;

    setModalOpen(false);
    const url = generateHotelAffiliateLink({
      neighborhood: activeHotel.location,
      city: activeParams.city,
      cityEn: activeParams.cityEn,
      checkin: activeParams.checkin,
      checkout: activeParams.checkout,
      guests: activeParams.guests,
      children: activeParams.children,
      childrenAges: activeParams.childrenAges,
      rooms: activeParams.rooms,
      language: activeParams.language,
      stars: activeHotel.stars,
      hotelType: activeHotel.type,
    });

    if (!safePartnerUrlForType(url, 'hotel')) {
      toast.error('Няма наличен хотелски партньор за тази дестинация. Моля, сменете дестинацията.');
      return;
    }

    // Fire conversion-funnel event tagged with the A/B variant. Combined with
    // the `ab_assign` event from getBookingLangVariant(), GA can compute the
    // booking-click rate per cohort.
    if (activeParams.language === 'bg') {
      trackTPEvent('booking_redirect', {
        partner: 'booking.com',
        type: 'hotel',
        ab_test: 'booking_lang',
        ab_variant: getBookingLangVariant('bg'),
        city: activeParams.cityEn || activeParams.city,
        stars: activeHotel.stars || activeHotel.type,
      });
    }

    performRedirect(url, {
      partner: 'booking',
      type: 'hotel',
      origin: null,
      destination: activeParams?.cityEn || activeParams?.city || null,
      price: activeHotel?.priceNumeric ?? null,
      surface: activeParams?.surface || 'hotel',
      item_id: activeHotel?.id || activeHotel?.name || null,
      reason_chips: activeHotel?.reasonChips || null,
      // iter80 — richer summary for the premium RedirectOverlay
      itemName: activeHotel?.name || null,
      itemImage: activeHotel?.image || null,
      itemRating: activeHotel?.rating || null,
      itemStars: activeHotel?.stars || null,
      priceDisplay: activeHotel?.price || null,
      checkin: activeParams?.checkin || null,
      checkout: activeParams?.checkout || null,
      guests: activeParams?.guests || null,
      rooms: activeParams?.rooms || null,
    });
  }, [activeHotel, activeParams, performRedirect, toast]);

  /** One-step flight booking (no modal — direct overlay + redirect) */
  const bookFlight = useCallback((flight) => {
    trackBookingClick({
      type: 'flight',
      route: `${flight?.departure}-${flight?.arrival}`,
      price: flight?.priceNumeric ?? flight?.price,
    });
    const url = generateFlightAffiliateLink({
      origin: flight?.departure,
      destination: flight?.arrival,
      departureDate: flight?.departure_at,
      returnDate: flight?.return_at,
      deepLink: flight?.deepLink || flight?.link,
      airline: flight?.airline,
      stops: flight?.stops,
      // BUG #1 FIX: forward party size to Aviasales URL builder so a
      // family-of-4 booking doesn't land on a 1-passenger search page.
      passengers: flight?.passengers || flight?.travelers,
    });
    performRedirect(url, {
      partner: 'aviasales',
      type: 'flight',
      origin: flight?.departure || null,
      destination: flight?.arrival || null,
      price: flight?.priceNumeric ?? (typeof flight?.price === 'number' ? flight.price : null),
      surface: flight?.surface || 'flight',
      item_id: flight?.id || `${flight?.departure}-${flight?.arrival}`,
      reason_chips: flight?.reasonChips || null,
      // iter80 — richer summary for the premium RedirectOverlay
      itemName: flight?.airline || null,
      priceDisplay: flight?.price || null,
      departureDate: flight?.departure_at || null,
      returnDate: flight?.return_at || null,
      stops: flight?.stops,
      duration: flight?.duration || null,
      passengers: flight?.passengers || flight?.travelers || null,
    });
  }, [performRedirect]);

  /**
   * One-step hotel booking — used by DestinationDetail's distributor buttons
   * where the user has ALREADY chosen the partner (Booking / Hotels.com /
   * Agoda / Trivago) so the trust-preview modal is redundant. Skips the
   * modal, jumps straight to the overlay + redirect using the deepLink
   * provided by the caller.
   */
  /**
   * One-step hotel booking — used by DestinationDetail's distributor buttons
   * where the user has ALREADY chosen the partner (Booking / Hotels.com /
   * Agoda / Trivago) so the trust-preview modal is redundant. Skips the
   * modal, jumps straight to the overlay + redirect using the deepLink
   * provided by the caller.
   */

  // === Sequential package stepper helpers ===
  // These open the partner site in a NEW TAB so the stepper UI stays alive
  // in the original tab. window.open MUST be called synchronously in the
  // user-gesture handler (no setTimeout/await before it) — otherwise mobile
  // browsers silently block the popup.
  const openFlightInNewTab = useCallback((flight) => {
    trackBookingClick({
      type: 'flight',
      route: `${flight?.departure}-${flight?.arrival}`,
      price: flight?.priceNumeric ?? flight?.price,
      context: 'package_stepper',
    });
    const url = generateFlightAffiliateLink({
      origin: flight?.departure,
      destination: flight?.arrival,
      departureDate: flight?.departure_at,
      returnDate: flight?.return_at,
      deepLink: flight?.deepLink || flight?.link,
      airline: flight?.airline,
      stops: flight?.stops,
      passengers: flight?.passengers || flight?.travelers,
    });
    const partnerUrl = safePartnerUrlForType(url, 'flight');
    if (!partnerUrl) {
      toast.error('Линкът за резервация на полета не е наличен. Опитайте друг полет.');
      return false;
    }
    try {
      trackAffiliateRedirect({
        partner: 'aviasales',
        type: 'flight',
        origin: flight?.departure || null,
        destination: flight?.arrival || null,
        price: flight?.priceNumeric ?? (typeof flight?.price === 'number' ? flight.price : null),
        marker: AFFILIATE_MARKER || null,
        surface: 'package_stepper',
        item_id: flight?.id || `${flight?.departure}-${flight?.arrival}`,
        reason_chips: flight?.reasonChips || null,
      });
    } catch { /* analytics must never block navigation */ }
    // Popup blockers may return null without throwing. Recover in the same tab.
    try {
      // Open a blank tab while the user gesture is active. Set opener=null
      // before handing it to the external supplier; avoids duplicate tabs
      // from browsers returning null for the 'noopener' window feature.
      const opened = window.open('about:blank', '_blank');
      if (opened) {
        opened.opener = null;
        opened.location.replace(partnerUrl);
      } else {
        toast.warning('Браузърът блокира новия раздел на партньора. Отваряме го в този раздел.');
        window.location.assign(partnerUrl);
      }
    } catch {
      window.location.assign(partnerUrl);
    }
    return true;
  }, [toast]);

  const openHotelInNewTab = useCallback((hotel, params) => {
    trackBookingClick({
      type: 'hotel',
      hotel_name: hotel?.name,
      city: params?.city,
      price: hotel?.priceNumeric ?? hotel?.price,
      context: 'package_stepper',
    });
    const url = hotel?.deepLink || generateHotelAffiliateLink({
      neighborhood: hotel?.location,
      city: params?.city,
      cityEn: params?.cityEn,
      checkin: params?.checkin,
      checkout: params?.checkout,
      guests: params?.guests,
      children: params?.children,
      childrenAges: params?.childrenAges,
      rooms: params?.rooms,
      language: params?.language,
      stars: params?.stars ?? hotel?.stars,
      hotelType: hotel?.type,
    });
    const partnerUrl = safePartnerUrlForType(url, 'hotel');
    if (!partnerUrl) {
      toast.error('Линкът за резервация на хотела не е наличен. Опитайте друг хотел.');
      return false;
    }
    try {
      trackAffiliateRedirect({
        partner: partnerNameFromUrl(partnerUrl) || 'unknown',
        type: 'hotel',
        origin: null,
        destination: params?.cityEn || params?.city || null,
        price: hotel?.priceNumeric ?? (typeof hotel?.price === 'number' ? hotel.price : null),
        marker: AFFILIATE_MARKER || null,
        surface: 'package_stepper',
        item_id: hotel?.id || hotel?.name || null,
        reason_chips: hotel?.reasonChips || null,
      });
    } catch { /* analytics must never block navigation */ }
    // Popup blockers may return null without throwing. Recover in the same tab.
    try {
      // Open a blank tab while the user gesture is active. Set opener=null
      // before handing it to the external supplier; avoids duplicate tabs
      // from browsers returning null for the 'noopener' window feature.
      const opened = window.open('about:blank', '_blank');
      if (opened) {
        opened.opener = null;
        opened.location.replace(partnerUrl);
      } else {
        toast.warning('Браузърът блокира новия раздел на партньора. Отваряме го в този раздел.');
        window.location.assign(partnerUrl);
      }
    } catch {
      window.location.assign(partnerUrl);
    }
    return true;
  }, [toast]);

  const bookHotel = useCallback((hotel, params) => {
    trackBookingClick({
      type: 'hotel',
      hotel_name: hotel?.name,
      city: params?.city,
      price: hotel?.priceNumeric ?? hotel?.price,
    });
    // Caller already passed the partner-specific deepLink. Fall back to the
    // generic Booking.com link if not supplied (defensive).
    const url = hotel?.deepLink || generateHotelAffiliateLink({
      neighborhood: hotel?.location,
      city: params?.city,
      cityEn: params?.cityEn,
      checkin: params?.checkin,
      checkout: params?.checkout,
      guests: params?.guests,
      children: params?.children,
      childrenAges: params?.childrenAges,
      rooms: params?.rooms,
      language: params?.language,
      stars: params?.stars ?? hotel?.stars,
      hotelType: hotel?.type,
    });
    performRedirect(url, {
      partner: partnerNameFromUrl(url) || 'unknown',
      type: 'hotel',
      origin: null,
      destination: params?.cityEn || params?.city || null,
      price: hotel?.priceNumeric ?? (typeof hotel?.price === 'number' ? hotel.price : null),
      surface: params?.surface || 'hotel',
      item_id: hotel?.id || hotel?.name || null,
      reason_chips: hotel?.reasonChips || null,
      // iter80 — richer summary for the premium RedirectOverlay
      itemName: hotel?.name || null,
      itemImage: hotel?.image || null,
      itemRating: hotel?.rating || null,
      itemStars: hotel?.stars || null,
      priceDisplay: hotel?.price || null,
      checkin: params?.checkin || null,
      checkout: params?.checkout || null,
      guests: params?.guests || null,
      rooms: params?.rooms || null,
    });
  }, [performRedirect]);

  return {
    // hotel flow (modal preview + confirm + redirect)
    openCheckoutPreview,
    closeCheckoutPreview,
    confirmBooking,
    // direct hotel redirect (skips modal — used when partner is pre-chosen)
    bookHotel,
    // flight flow
    bookFlight,
    // sequential stepper helpers (open partner in NEW TAB so the stepper UI
    // remains alive in the original tab, enabling guided multi-segment booking)
    openFlightInNewTab,
    openHotelInNewTab,
    // overlay control (lets consumers dismiss if user taps the overlay or fallback)
    dismissOverlay,
    // auth gate
    authGateOpen,
    closeAuthGate,
    openAuthGate,
    // profile gate (auth'd but missing phone)
    profileGateOpen,
    closeProfileGate,
    onProfileComplete,
    // state
    modalOpen,
    overlayVisible,
    pendingUrl,
    redirectContext,
    activeHotel,
    activeParams,
  };
}
